import React, {useState, useEffect} from "react";
import ErrorFallback from "./ErrorFallback";
import { Navigate, useLocation } from "react-router-dom";
import {
  apiCallErrorAtom,
  authAtom,
  DEFAULT_AUTH_ATOM_VALUE,
} from "../../state";
import { useRecoilValue, useSetRecoilState } from "recoil";
import Error404Image from "./svgs/404.svg";
import Error500Image from "./svgs/500.svg";
import GenericErrorImage from "./svgs/GenericError.svg";

class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error boundary", error, errorInfo);
  }

  createErrorFallback(errorMessage, errorImage) {
    return <ErrorFallback error={errorMessage} image={errorImage} />;
  }

  render() {
    const { setAuth, apiCallError, location } = this.props;

    let errorCode = apiCallError.httpCode;
    let errorMessage = apiCallError.message;

    // Check if we have an error that came through the error boundary
    if (!errorCode && this.state.hasError) {
      errorCode = -1;
      errorMessage = this.state.error.toString();
    }

    if (errorCode) {
      switch (errorCode) {
        case 401:
        case 403:
          setAuth(DEFAULT_AUTH_ATOM_VALUE);
          return <Navigate to="/login" state={{ from: location }} replace />;
        case 404:
          return this.createErrorFallback(errorMessage, Error404Image);
        case 500:
          return this.createErrorFallback(errorMessage, Error500Image);
        default:
          return this.createErrorFallback(errorMessage, GenericErrorImage);
      }
    }

    return this.props.children;
  }
}

const ErrorBoundary = ({ children }) => {
  const setAuth = useSetRecoilState(authAtom);
  const apiCallError = useRecoilValue(apiCallErrorAtom);
  const location = useLocation();
  const [path, setPath] = useState(location.pathname);

  useEffect(
    () => setPath(location.pathname),
    [location.pathname]
  );

  return (
    <ErrorBoundaryInner
      setAuth={setAuth}
      apiCallError={apiCallError}
      location={location}
      key={path}
    >
      {children}
    </ErrorBoundaryInner>
  );
};

export default ErrorBoundary;
